import React from 'react';

interface GradeActionsProps {
  onSave: () => void;
  onSubmitToAdmin?: () => void;
  onPublish?: () => void;
  saveDisabled?: boolean;
  publishDisabled?: boolean;
  submissionStatus?: {
    hasGrades: boolean;
    allSubmitted: boolean;
    allPublished: boolean;
    gradeCount: number;
  };
  isExamGrades?: boolean;
}

export const GradeActions: React.FC<GradeActionsProps> = ({
  onSave,
  onSubmitToAdmin,
  onPublish,
  saveDisabled = false,
  publishDisabled = false,
  submissionStatus,
  isExamGrades = false,
}) => {
  const getSubmitButtonState = () => {
    if (!submissionStatus) return { bg: '#6c757d', text: 'SAVE FIRST', disabled: true };

    const { hasGrades, allSubmitted, allPublished, gradeCount } = submissionStatus;

    if (!hasGrades) {
      return { bg: '#6c757d', text: 'SAVE GRADES FIRST', disabled: true };
    }

    if (allPublished) {
      return {
        bg: '#28a745',
        text: `✓ PUBLISHED (${gradeCount})`,
        disabled: true,
      };
    }

    if (allSubmitted) {
      return {
        bg: '#17a2b8',
        text: `✓ SUBMITTED (${gradeCount}) - Awaiting Admin`,
        disabled: true,
      };
    }

    return {
      bg: '#2d7a3d',
      text: 'SUBMIT TO ADMIN',
      disabled: false,
    };
  };

  const submitState = getSubmitButtonState();

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
      {/* Save Button */}
      <button
        type="button"
        onClick={onSave}
        disabled={saveDisabled}
        style={{
          padding: '12px 28px',
          background: saveDisabled ? '#ccc' : '#3e3e3e',
          color: '#fff',
          borderRadius: 6,
          border: 'none',
          cursor: saveDisabled ? 'not-allowed' : 'pointer',
          fontWeight: 700,
          fontSize: 14,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxShadow: saveDisabled ? 'none' : '0 2px 6px rgba(0,0,0,0.15)',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!saveDisabled) {
            e.currentTarget.style.background = '#2d2d2d';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!saveDisabled) {
            e.currentTarget.style.background = '#3e3e3e';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
          }
        }}
      >
        💾 Save Grades
      </button>

      {/* Submit to Admin Button (for exam grades) */}
      {isExamGrades && onSubmitToAdmin && (
        <button
          type="button"
          onClick={onSubmitToAdmin}
          disabled={submitState.disabled}
          style={{
            padding: '12px 28px',
            background: submitState.disabled ? '#ccc' : submitState.bg,
            color: '#fff',
            borderRadius: 6,
            border: 'none',
            cursor: submitState.disabled ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: submitState.disabled ? 'none' : '0 2px 6px rgba(0,0,0,0.15)',
            transition: 'all 0.2s',
            opacity: submitState.disabled ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!submitState.disabled) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!submitState.disabled) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
            }
          }}
        >
          {submitState.text}
        </button>
      )}

      {/* Publish Button (for assignment grades) */}
      {!isExamGrades && onPublish && (
        <button
          type="button"
          onClick={onPublish}
          disabled={publishDisabled}
          style={{
            padding: '12px 28px',
            background: publishDisabled ? '#ccc' : '#2d7a3d',
            color: '#fff',
            borderRadius: 6,
            border: 'none',
            cursor: publishDisabled ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: publishDisabled ? 'none' : '0 2px 6px rgba(0,0,0,0.15)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!publishDisabled) {
              e.currentTarget.style.background = '#246a32';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!publishDisabled) {
              e.currentTarget.style.background = '#2d7a3d';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
            }
          }}
        >
          📤 Publish to Students
        </button>
      )}
    </div>
  );
};
